/** Fallback quando nenhuma cor de branding tem contraste adequado */
const DEFAULT_ACTION_PRIMARY = '#1976d2';

/** Fundo claro padrão usado para validar contraste de botões */
const DEFAULT_LIGHT_BACKGROUND = '#ffffff';

/** Contraste mínimo recomendado para ações em fundo claro */
const MIN_ACTION_CONTRAST_RATIO = 3;

export interface IResolveActionPrimaryInput {
  primary: string;
  primaryFocus: string;
  sidebarStart: string;
}

/**
 * Normaliza valor hex (remove aspas, expande shorthand #fff → #ffffff)
 */
export function normalizeHex(color: string): string | null {
  const trimmed = color.trim().replace(/^["']|["']$/g, '');

  if (!trimmed.startsWith('#')) {
    return null;
  }

  const hex = trimmed.slice(1);

  if (hex.length === 3) {
    return `#${hex
      .split('')
      .map((char) => `${char}${char}`)
      .join('')}`;
  }

  if (hex.length === 6) {
    return `#${hex.toLowerCase()}`;
  }

  return null;
}

/**
 * Calcula luminância relativa WCAG de uma cor hex
 */
export function getRelativeLuminance(hexColor: string): number {
  const normalized = normalizeHex(hexColor);

  if (!normalized) {
    return 0;
  }

  const channels = [1, 3, 5].map((index) =>
    parseInt(normalized.slice(index, index + 2), 16) / 255,
  );

  const [red, green, blue] = channels.map((channel) =>
    channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4,
  );

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/**
 * Calcula razão de contraste entre duas cores
 */
export function getContrastRatio(foreground: string, background: string): number {
  const fgLuminance = getRelativeLuminance(foreground);
  const bgLuminance = getRelativeLuminance(background);
  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Verifica se a cor tem contraste insuficiente sobre fundo claro
 */
export function isColorTooLightForBackground(
  color: string,
  background: string = DEFAULT_LIGHT_BACKGROUND,
  minRatio: number = MIN_ACTION_CONTRAST_RATIO,
): boolean {
  const normalized = normalizeHex(color);

  if (!normalized) {
    return true;
  }

  const ratio =
    Math.round(getContrastRatio(normalized, background) * 100) / 100;

  return ratio < minRatio;
}

/**
 * Retorna cor de texto legível sobre um fundo sólido
 */
export function getContrastText(backgroundColor: string): string {
  const normalized = normalizeHex(backgroundColor);

  if (!normalized) {
    return '#ffffff';
  }

  const whiteContrast = getContrastRatio('#ffffff', normalized);
  const darkContrast = getContrastRatio('#1a1a1a', normalized);

  return whiteContrast >= darkContrast ? '#ffffff' : '#1a1a1a';
}

/**
 * Escolhe a primeira cor com contraste adequado para botões e links
 */
export function resolveActionPrimary({
  primary,
  primaryFocus,
  sidebarStart,
}: IResolveActionPrimaryInput): string {
  const candidates = [primary, primaryFocus, sidebarStart, DEFAULT_ACTION_PRIMARY];

  for (const candidate of candidates) {
    const normalized = normalizeHex(candidate);

    if (!normalized) {
      continue;
    }

    if (!isColorTooLightForBackground(normalized)) {
      return normalized;
    }
  }

  return DEFAULT_ACTION_PRIMARY;
}
